import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ClockCounterClockwise, Spinner, User, FunnelSimple, CalendarBlank, Export, Bank, Money, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Pagination from "../components/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerActivityPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [filterStaff, setFilterStaff] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const [filterPayment, setFilterPayment] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const actionLabels = {
    apartment_created: t("activity.apartment_created"),
    tenant_created: t("activity.tenant_created"),
    device_created: t("activity.device_created"),
    balance_added: t("activity.balance_added"),
    tenant_removed: t("activity.tenant_removed"),
    tenant_changed: t("activity.tenant_changed"),
    tenant_deleted: t("activity.tenant_deleted"),
    tenant_block: t("activity.tenant_block"),
    invoice_generated: t("activity.invoice_generated"),
    invoice_paid: t("activity.invoice_paid"),
    invoice_deleted: t("activity.invoice_deleted"),
    invoice_restored: t("activity.invoice_restored"),
    settings_updated: t("activity.settings_updated"),
    fixed_expense_exempt: t("activity.fixed_expense_exempt"),
    low_balance_check: t("activity.low_balance_check"),
    customer_status_change: t("activity.customer_status_change"),
    topup_approved: "Yukleme Onaylandi",
    topup_rejected: "Yukleme Reddedildi",
  };

  const paymentLabels = { bank: "Havale/EFT", cash: "Nakit", manual: "Manuel", card: "Kredi Karti" };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStaff !== "all") params.append("staff_id", filterStaff);
      if (filterAction !== "all") params.append("action", filterAction);
      if (filterPayment !== "all") params.append("payment_method", filterPayment);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      const res = await axios.get(`${API_URL}/api/customer/activity-logs?${params}`);
      setLogs(res.data);

      const uniqueStaff = [];
      const seen = new Set();
      res.data.forEach((log) => {
        if (log.user_id && !seen.has(log.user_id)) {
          seen.add(log.user_id);
          uniqueStaff.push({ id: log.user_id, name: log.user_name || log.user_id });
        }
      });
      if (staffList.length === 0) setStaffList(uniqueStaff);
    } catch {
      toast.error(t("activity.logs_error"));
    } finally {
      setLoading(false);
    }
  }, [filterStaff, filterAction, filterPayment, startDate, endDate, t, staffList.length]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const roleBadge = (role) => {
    const colors = {
      customer: "bg-indigo-100 text-indigo-700",
      staff: "bg-amber-100 text-amber-700",
      admin: "bg-red-100 text-red-700",
    };
    return colors[role] || "bg-slate-100 text-slate-600";
  };

  const uniqueActions = [...new Set(logs.map((l) => l.action))];

  const handleExportCSV = () => {
    if (logs.length === 0) return;
    const headers = ["Tarih", "Kullanici", "Rol", "Islem", "Odeme Yontemi", "Detay"];
    const rows = logs.map((log) => [
      new Date(log.created_at).toLocaleString("tr-TR"),
      log.user_name || "-",
      log.role || "-",
      actionLabels[log.action] || log.action,
      paymentLabels[log.payment_method] || "-",
      (log.detail || "").replace(/,/g, ";"),
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `islem-raporu-${startDate || "tum"}-${endDate || "tum"}.csv`;
    link.click();
  };

  const clearFilters = () => {
    setFilterStaff("all");
    setFilterAction("all");
    setFilterPayment("all");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  // Summary stats
  const totalApproved = logs.filter((l) => l.action === "topup_approved").length;
  const totalRejected = logs.filter((l) => l.action === "topup_rejected").length;
  const cashCount = logs.filter((l) => l.payment_method === "cash").length;
  const bankCount = logs.filter((l) => l.payment_method === "bank").length;
  const manualCount = logs.filter((l) => l.payment_method === "manual").length;

  return (
    <div className="space-y-6" data-testid="customer-activity-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">{t("activity.customer_title")}</h1>
          <p className="text-slate-500 mt-1">{t("activity.customer_subtitle")}</p>
          <span className="inline-flex items-center text-xs text-slate-400 mt-1" data-testid="retention-info">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            Son 90 gün gösteriliyor
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={logs.length === 0} data-testid="export-csv-btn">
            <Export size={16} className="mr-1" /> CSV
          </Button>
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
            <ArrowsClockwise size={16} className="mr-1" /> Temizle
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-slate-50 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-slate-900">{logs.length}</p>
          <p className="text-xs text-slate-500">Toplam Islem</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-emerald-700">{totalApproved}</p>
          <p className="text-xs text-emerald-600">Onaylanan</p>
        </div>
        <div className="bg-red-50 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-red-700">{totalRejected}</p>
          <p className="text-xs text-red-600">Reddedilen</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-blue-700">{bankCount}</p>
          <p className="text-xs text-blue-600">Havale/EFT</p>
        </div>
        <div className="bg-green-50 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-green-700">{cashCount + manualCount}</p>
          <p className="text-xs text-green-600">Nakit/Manuel</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <FunnelSimple size={18} className="text-slate-400" />
            <span className="text-sm font-medium text-slate-700">Filtreler</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="activity-filters">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Calisan</Label>
              <Select value={filterStaff} onValueChange={setFilterStaff}>
                <SelectTrigger className="h-9 text-xs" data-testid="filter-staff">
                  <SelectValue placeholder="Tum Calisanlar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tum Calisanlar</SelectItem>
                  {staffList.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Islem Tipi</Label>
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger className="h-9 text-xs" data-testid="filter-action">
                  <SelectValue placeholder="Tum Islemler" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tum Islemler</SelectItem>
                  {uniqueActions.map((a) => (
                    <SelectItem key={a} value={a}>{actionLabels[a] || a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Odeme Yontemi</Label>
              <Select value={filterPayment} onValueChange={setFilterPayment}>
                <SelectTrigger className="h-9 text-xs" data-testid="filter-payment">
                  <SelectValue placeholder="Tum Yontemler" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tum Yontemler</SelectItem>
                  <SelectItem value="bank">Havale/EFT</SelectItem>
                  <SelectItem value="cash">Nakit</SelectItem>
                  <SelectItem value="manual">Manuel Yukleme</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500 flex items-center gap-1"><CalendarBlank size={12} /> Baslangic</Label>
              <Input type="date" className="h-9 text-xs" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="filter-start-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500 flex items-center gap-1"><CalendarBlank size={12} /> Bitis</Label>
              <Input type="date" className="h-9 text-xs" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="filter-end-date" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity List */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Spinner size={28} className="animate-spin text-indigo-600" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <ClockCounterClockwise size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">{t("activity.no_logs")}</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {logs.slice((page - 1) * pageSize, page * pageSize).map((log) => (
                <div key={log.id} className="flex items-start gap-4 p-4 hover:bg-slate-50" data-testid={`log-${log.id}`}>
                  <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User size={18} className="text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900">{log.user_name || t("staff.system")}</span>
                      <Badge className={`text-[10px] ${roleBadge(log.role)}`}>{log.role}</Badge>
                      <span className="text-slate-400">-</span>
                      <span className="text-sm text-slate-700 font-medium">{actionLabels[log.action] || log.action}</span>
                      {log.payment_method && (
                        <Badge className={`text-[10px] ${log.payment_method === "cash" ? "bg-green-100 text-green-700" : log.payment_method === "bank" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                          {log.payment_method === "cash" ? <Money size={10} className="mr-0.5" /> : log.payment_method === "bank" ? <Bank size={10} className="mr-0.5" /> : null}
                          {paymentLabels[log.payment_method] || log.payment_method}
                        </Badge>
                      )}
                    </div>
                    {log.detail && <p className="text-sm text-slate-500 mt-0.5">{log.detail}</p>}
                    <p className="text-xs text-slate-400 mt-1">{new Date(log.created_at).toLocaleString("tr-TR")}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      {logs.length > pageSize && (
        <Pagination total={logs.length} page={page} pageSize={pageSize}
          onPageChange={setPage} onPageSizeChange={setPageSize} />
      )}
    </div>
  );
};

export default CustomerActivityPage;
